// src/analytics/ids.js
import { getCookie, setCookie } from "../../assets/utils/cookies";

const VID = "td_vid";
const SID = "td_sid";
const ATTR = "td_attr";

const UTM_KEYS = [
  "utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content",
  "gclid", "fbclid", "msclkid",
];

// Captured once per full page load, before any SPA route change can rewrite
// the URL. Held in memory only — nothing is written to a cookie until
// consent exists (see getOrCreateAttribution below).
const landingAttribution = (() => {
  const params = new URLSearchParams(window.location.search);
  const found = {};
  for (const key of UTM_KEYS) {
    const v = params.get(key);
    if (v) found[key] = v;
  }
  return {
    ...found,
    landing_page: window.location.pathname,
    referrer: document.referrer || null,
  };
})();

// Raw, ephemeral, non-identifying landing info — safe to include on the
// anonymous/forced pre-consent event since nothing is persisted from it.
export function getLandingAttribution() {
  return landingAttribution;
}

// First-touch campaign attribution, persisted only once analytics consent
// exists. Returns null when there's nothing campaign-related to attribute
// (direct/organic visits) so payloads stay clean.
export function getOrCreateAttribution() {
  const existing = getCookie(ATTR);
  if (existing) {
    try {
      return JSON.parse(existing);
    } catch {
      // fall through and re-derive below
    }
  }
  const hasUtm = UTM_KEYS.some(key => landingAttribution[key]);
  if (!hasUtm) return null;
  setCookie(ATTR, JSON.stringify(landingAttribution), { days: 180, sameSite: "Lax" });
  return landingAttribution;
}

// simple random id (good enough for analytics)
function rid(len = 16) {
  const bytes = new Uint8Array(len);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, b => b.toString(16).padStart(2, "0")).join("");
}

export function getOrCreateVisitorId() {
  let vid = getCookie(VID);
  if (!vid) {
    vid = "v_" + rid(16);
    setCookie(VID, vid, { days: 180, sameSite: "Lax" });
  }
  return vid;
}

/**
 * Session cookie: short-lived. We'll use a cookie that expires quickly
 * and refresh it on activity. If it disappears, we treat as new session.
 */
export function getOrCreateSessionId() {
  let sid = getCookie(SID);
  if (!sid) sid = "s_" + rid(12);
  // "days" is the easiest knob in our helper; use a tiny fraction of a day:
  // 0.02 days ≈ 28.8 minutes
  setCookie(SID, sid, { days: 0.02, sameSite: "Lax" });
  return sid;
}

// call this on activity to extend session window
export function touchSession() {
  const sid = getCookie(SID);
  if (sid) setCookie(SID, sid, { days: 0.02, sameSite: "Lax" });
}
